import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, KeyRound, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, V1 } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || null;

  // SMS OTP flow
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState("phone"); // phone -> code
  const [sending, setSending] = useState(false);

  // Password flow
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const sendCode = async () => {
    setSending(true);
    try {
      await api.post(`${V1}/auth/send-code`, { phone, purpose: "login" });
      setStep("code");
      toast.success("Doğrulama kodu gönderildi");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "SMS gönderilemedi");
    } finally { setSending(false); }
  };

  const verify = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`${V1}/auth/verify-code`, { phone, code, name: name || undefined });
      await login(data.token, data.user);
      toast.success("Hoş geldiniz!");
      navigate(from || "/panel");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kod doğrulanamadı");
    } finally { setSending(false); }
  };

  const loginPass = async () => {
    setSending(true);
    try {
      const { data } = await api.post(`${V1}/auth/login`, { email, password });
      await login(data.token, data.user);
      toast.success("Giriş başarılı");
      const dest = data.user.role === "admin" ? "/admin" : data.user.role === "school" ? "/okul-paneli" : "/panel";
      navigate(from || dest);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Giriş yapılamadı");
    } finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-md px-4 py-16" data-testid="login-page">
      <div className="text-center mb-8">
        <div className="overline text-accent">Giriş Yap</div>
        <h1 className="font-display text-3xl font-bold mt-2">Tekrar hoş geldin.</h1>
      </div>
      <div className="rounded-xl border border-border bg-card p-6">
        <Tabs defaultValue="sms">
          <TabsList className="w-full grid grid-cols-2 mb-6">
            <TabsTrigger value="sms" data-testid="login-tab-sms"><Phone className="h-4 w-4 mr-1" /> SMS ile</TabsTrigger>
            <TabsTrigger value="pass" data-testid="login-tab-pass"><Mail className="h-4 w-4 mr-1" /> E-posta</TabsTrigger>
          </TabsList>
          <TabsContent value="sms">
            {step === "phone" ? (
              <div className="space-y-4">
                <div>
                  <Label>Cep Telefonu</Label>
                  <Input placeholder="5XX XXX XX XX" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="login-phone-input" />
                  <div className="text-xs text-muted-foreground mt-1">Türk cep numarası (5 ile başlar)</div>
                </div>
                <div>
                  <Label>Ad Soyad (yeni kayıt için)</Label>
                  <Input placeholder="Ad Soyad" value={name} onChange={(e) => setName(e.target.value)} data-testid="login-name-input" />
                </div>
                <Button onClick={sendCode} disabled={sending || phone.length < 10} className="w-full" data-testid="send-code-btn">
                  {sending ? "Gönderiliyor…" : "Kodu Gönder"} <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>SMS Doğrulama Kodu</Label>
                  <Input placeholder="6 haneli kod" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} data-testid="login-code-input" />
                  <div className="text-xs text-muted-foreground mt-1">{phone} numarasına gönderildi. 5 dk geçerlidir.</div>
                </div>
                <Button onClick={verify} disabled={sending || code.length < 4} className="w-full" data-testid="verify-code-btn">
                  {sending ? "Doğrulanıyor…" : "Doğrula ve Giriş Yap"}
                </Button>
                <button className="text-xs text-muted-foreground hover:text-foreground" onClick={() => setStep("phone")} type="button">← Numarayı değiştir</button>
              </div>
            )}
          </TabsContent>
          <TabsContent value="pass">
            <div className="space-y-4">
              <div>
                <Label>E-posta</Label>
                <Input type="email" placeholder="ornek@sanostudent.com" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
              </div>
              <div>
                <Label>Şifre</Label>
                <Input type="password" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
              </div>
              <Button onClick={loginPass} disabled={sending} className="w-full" data-testid="login-password-btn">
                {sending ? "Giriş yapılıyor…" : "Giriş Yap"}
              </Button>
              <div className="text-xs text-muted-foreground text-center">Yönetici ve okul hesapları için</div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <div className="text-center text-sm text-muted-foreground mt-6">
        Hesabın yok mu? <Link to="/kayit" className="text-primary font-medium hover:underline">Ücretsiz kayıt ol</Link>
      </div>
    </div>
  );
}
